import { Form, Link, useNavigate, useRouteLoaderData } from "react-router-dom";
import { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import UserInfo from "src/components/common/userInfo";
import CurrentMaterialCost from "src/components/common/currentMaterialCost";
import { useUiStore } from "src/components/main/uiStore";
import {
    mimeTypeMap,
    myAxios,
    numberReducer,
    MyDate,
} from "src/components/common/functions";
import {
    Billing,
    BillingAssignment,
    defaultBilling,
} from "./types";

type InvalidRange = {
    billingNumber: string;
    fromDate: string;
    toDate: string;
};

export default function BillingForm({ keyword }: { keyword: "Add" | "Edit" }) {
    const billing = (useRouteLoaderData("Billing") as Billing | undefined) ?? defaultBilling;
    const navigate = useNavigate();
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const [assignments, setAssignments] = useState<BillingAssignment[]>([]);
    const [curBillingNumber, setBillingNumber] = useState(billing.billingNumber ?? "");
    const [curProjectId, setProjectId] = useState(billing.projectId ?? "");
    const [curProjectName, setProjectName] = useState(billing.projectName ?? "");
    const [curAmount, setAmount] = useState(billing.amount ?? "");
    const [curBillable, setBillable] = useState(billing.billable ?? "yes");
    const [curStartDate, setStartDate] = useState(billing.fromDate ?? "");
    const [curEndDate, setEndDate] = useState(billing.toDate ?? "");
    const [curApprover, setApprover] = useState<SelectOption | null>(
        billing.approver?.value || billing.approver?.label
            ? { value: billing.approver.value, label: billing.approver.label }
            : null
    );
    const [invalidText, setInvalidText] = useState<InvalidRange | null>(null);

    useEffect(() => {
        setApprover(
            billing.approver?.value || billing.approver?.label
                ? { value: billing.approver.value, label: billing.approver.label }
                : null
        );
    }, [billing.approver]);

    useEffect(() => {
        if (!curProjectId) {
            setAssignments([]);
            return;
        }
        let isMounted = true;
        const controller = new AbortController();
        const loadAssignments = async () => {
            const formData = new FormData();
            formData.append("projectId", curProjectId);
            setShowLoader(true);
            try {
                const res = await myAxios({
                    fileName: "billings/readBillingAllAssignments.php",
                    formData,
                    signal: controller.signal,
                });
                if (!isMounted || !res) return;
                setAssignments(Array.isArray(res.data) ? (res.data as BillingAssignment[]) : []);
            } finally {
                if (isMounted) setShowLoader(false);
            }
        };
        loadAssignments();
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [curProjectId, setShowLoader]);

    const closeIt = () => {
        if (keyword === "Edit" && billing.id) {
            navigate(`/Billings/${billing.id}`);
            return;
        }
        navigate("/Billings");
    };

    const dateMin = assignments.length ? assignments[0].startTime.substring(0, 10) : "";
    const dateMax = assignments.length ? assignments[assignments.length - 1].startTime.substring(0, 10) : "";

    const projectName = curProjectName || billing.projectName || "";

    return (
        <Form method="post" encType="multipart/form-data" className="pt-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 mb-3">
                <div>
                    <h4 className="mb-1">{keyword} Billing</h4>
                    <div className="text-muted">
                        {keyword === "Edit" ? billing.billingNumber : "Create a new billing record"}
                    </div>
                </div>
            </div>
            <div className="px-3 pb-4">
                {keyword === "Edit" ? <input className="nodisplay" readOnly name="id" value={billing.id ?? ""} /> : null}
                <input className="nodisplay" readOnly name="action" value={keyword} />
                <input className="nodisplay" readOnly name="pdfId" value={billing.pdfId ?? ""} />
                <div className="d-flex align-items-center my-2">
                    <label htmlFor="billingNumber" className="fw-bold required-field text-nowrap">
                        Billing Number:
                    </label>
                    <input
                        id="billingNumber"
                        required
                        className="form-control flex-grow-1 ms-2"
                        name="billingNumber"
                        value={curBillingNumber}
                        onChange={event => {
                            setBillingNumber(
                                event.currentTarget.value.replace(/ /g, "").replace(/\//g, "").replace(/"/g, "").replace(/'/g, "")
                            );
                        }}
                    />
                </div>
                <div className="d-flex align-items-center my-2">
                    <label htmlFor="projectName" className="fw-bold required-field text-nowrap">
                        Project Name:
                    </label>
                    <div className="flex-grow-1 ms-2">
                        <AsyncFormSelect
                            className="flex-grow-1"
                            placeholder="Search project..."
                            required={true}
                            name="projectId"
                            inputId="projectName"
                            fileName="common/searchProject.php"
                            value={curProjectId ? { label: projectName || curProjectId, value: curProjectId } : null}
                            onChange={next => {
                                setStartDate("");
                                setEndDate("");
                                setInvalidText(null);
                                setProjectId((next as SelectOption | null)?.value ?? "");
                                setProjectName((next as SelectOption | null)?.label ?? "");
                            }}
                        />
                    </div>
                </div>
                <div className="d-flex align-items-center my-2">
                    <label htmlFor="amount" className="fw-bold required-field text-nowrap">
                        Amount:
                    </label>
                    <input
                        id="amount"
                        required
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control flex-grow-1 ms-2"
                        name="amount"
                        value={curAmount}
                        onChange={event => setAmount(event.currentTarget.value)}
                    />
                </div>
                <div className="d-flex align-items-center my-2">
                    <label htmlFor="billable" className="fw-bold required-field text-nowrap">
                        Billable:
                    </label>
                    <select
                        id="billable"
                        required
                        className="form-control flex-grow-1 ms-2"
                        name="billable"
                        value={curBillable}
                        onChange={event => setBillable(event.currentTarget.value)}
                    >
                        <option value="yes">yes</option>
                        <option value="no">no</option>
                    </select>
                </div>
                {curProjectId ? (
                    <>
                        <CurrentMaterialCost
                            className=""
                            uid="inner"
                            id={curProjectId}
                            projectName={projectName}
                        />
                        <div>
                            <div className="d-flex align-items-center my-2">
                                <label htmlFor="fromDate" className="fw-bold required-field text-nowrap">
                                    Start Date:
                                </label>
                                <input
                                    id="fromDate"
                                    type="date"
                                    min={dateMin || "1900-01-01"}
                                    max={dateMax || "2999-12-31"}
                                    required
                                    className="form-control flex-grow-1 ms-2"
                                    name="fromDate"
                                    value={curStartDate}
                                    onChange={event => {
                                        setStartDate(event.currentTarget.value);
                                        setInvalidText(null);
                                    }}
                                />
                                <label htmlFor="toDate" className="fw-bold required-field ms-3 text-nowrap">
                                    End Date:
                                </label>
                                <input
                                    id="toDate"
                                    type="date"
                                    min={(curStartDate > dateMin ? curStartDate : dateMin) || "1900-01-01"}
                                    max={dateMax || "2999-12-31"}
                                    required
                                    className="form-control flex-grow-1 ms-2"
                                    name="toDate"
                                    value={curEndDate}
                                    onChange={event => {
                                        setEndDate(event.currentTarget.value);
                                        setInvalidText(null);
                                    }}
                                />
                            </div>
                            {invalidText ? (
                                <span className="mx-3 text-danger fw-bold">
                                    <span className="me-1">conflict with</span>
                                    <Link to={`/Billings/${invalidText.billingNumber}`}>
                                        from {invalidText.fromDate} to {invalidText.toDate}
                                    </Link>
                                </span>
                            ) : null}
                            <button
                                type="button"
                                className="ms-2 btn btn-success"
                                onClick={async () => {
                                    if (!window.confirm("Are you sure to download all Job Tags?")) return;
                                    const selectedAssignments = assignments.filter(
                                        assignment =>
                                            assignment.startTime.substring(0, 10) >= curStartDate &&
                                            assignment.startTime.substring(0, 10) <= curEndDate
                                    );
                                    if (!selectedAssignments.length) {
                                        window.alert("No Job Tags Selected. Select Job Tags By Entering Start Date and End Date Above.");
                                        return;
                                    }
                                    if (selectedAssignments.some(assignment => assignment.assignmentStatus === "Submitted")) {
                                        if (!window.confirm("You have unapproved Job Tags. Still proceed?")) return;
                                    }
                                    window.alert("WARNING: Verify there are no remaining Job Tags assigned to this BTL before proceeding.");
                                    const formData = new FormData();
                                    for (const assignment of selectedAssignments) {
                                        formData.append("fileId[]", assignment.jobTag);
                                        const duration =
                                            assignment.startTime.substring(0, 10) === assignment.endTime.substring(0, 10)
                                                ? assignment.startTime.substring(0, 10)
                                                : `from ${assignment.startTime.substring(0, 10)} to ${assignment.endTime.substring(0, 10)}`;
                                        formData.append("fileName[]", `${assignment.laborName} ${duration}.pdf`);
                                    }
                                    setShowLoader(true);
                                    const res = await myAxios({ fileName: "common/getFile.php", formData, responseType: "blob" });
                                    setShowLoader(false);
                                    if (!res) return;
                                    const now = new MyDate();
                                    if (selectedAssignments.length === 1) {
                                        saveAs(new Blob([res.data], { type: mimeTypeMap.pdf }), `${curBillingNumber} Downloaded at ${now.s()} ${now.ss()}.pdf`);
                                    } else {
                                        saveAs(new Blob([res.data], { type: mimeTypeMap.zip }), `${curBillingNumber} Downloaded at ${now.s()} ${now.ss()}.zip`);
                                    }
                                }}
                            >
                                Download Selection
                            </button>
                        </div>
                        <div className="table-responsive" style={{ minWidth: "800px", maxWidth: "90%", margin: "20px auto" }}>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Duration</th>
                                        <th>Technician</th>
                                        <th>Job Tag</th>
                                        <th>Status</th>
                                        <th>Total Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignments.map((renderedAssignment, index) => (
                                        <tr
                                            key={index}
                                            style={{
                                                background:
                                                    renderedAssignment.startTime.substring(0, 10) >= curStartDate &&
                                                    renderedAssignment.startTime.substring(0, 10) <= curEndDate
                                                        ? "#90EE90"
                                                        : "",
                                            }}
                                        >
                                            <td>
                                                <strong>
                                                    <span style={{ fontSize: 20 }}>{renderedAssignment.startTime.substring(0, 10)}</span>
                                                    {renderedAssignment.startTime.substring(11, 16) === "00:00" &&
                                                    renderedAssignment.endTime.substring(11, 16) === "00:00"
                                                        ? ""
                                                        : ` ${renderedAssignment.startTime.substring(11, 16)}`}
                                                    {renderedAssignment.startTime.substring(0, 10) === renderedAssignment.endTime.substring(0, 10) &&
                                                    renderedAssignment.startTime.substring(11, 16) === "00:00" &&
                                                    renderedAssignment.endTime.substring(11, 16) === "00:00"
                                                        ? ""
                                                        : " ~ "}
                                                    {renderedAssignment.startTime.substring(0, 10) === renderedAssignment.endTime.substring(0, 10) ? (
                                                        renderedAssignment.startTime.substring(11, 16) === "00:00" &&
                                                        renderedAssignment.endTime.substring(11, 16) === "00:00" ? (
                                                            ""
                                                        ) : (
                                                            renderedAssignment.endTime.substring(11, 16)
                                                        )
                                                    ) : renderedAssignment.startTime.substring(11, 16) === "00:00" &&
                                                      renderedAssignment.endTime.substring(11, 16) === "00:00" ? (
                                                        <span style={{ fontSize: 20 }}>{renderedAssignment.endTime.substring(0, 10)}</span>
                                                    ) : (
                                                        <>
                                                            <span style={{ fontSize: 20 }}>{renderedAssignment.endTime.substring(0, 10)} </span>
                                                            {renderedAssignment.endTime.substring(11, 16)}
                                                        </>
                                                    )}
                                                </strong>
                                            </td>
                                            <td>
                                                <UserInfo name={renderedAssignment.laborName} id={renderedAssignment.labor} />
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={async () => {
                                                        const formData = new FormData();
                                                        formData.append("fileId[]", renderedAssignment.jobTag);
                                                        const duration =
                                                            renderedAssignment.startTime.substring(0, 10) === renderedAssignment.endTime.substring(0, 10)
                                                                ? renderedAssignment.startTime.substring(0, 10)
                                                                : `from ${renderedAssignment.startTime.substring(0, 10)} to ${renderedAssignment.endTime.substring(0, 10)}`;
                                                        formData.append("fileName[]", `${renderedAssignment.laborName} ${duration}.pdf`);
                                                        setShowLoader(true);
                                                        const res = await myAxios({ fileName: "common/getFile.php", formData, responseType: "blob" });
                                                        setShowLoader(false);
                                                        if (!res) return;
                                                        saveAs(new Blob([res.data]), `${renderedAssignment.laborName} ${duration}.pdf`);
                                                    }}
                                                >
                                                    Download
                                                </button>
                                            </td>
                                            <td className={renderedAssignment.assignmentStatus === "Submitted" ? "text-danger" : ""}>
                                                {renderedAssignment.assignmentStatus}
                                            </td>
                                            <td>
                                                <li>
                                                    Travel:{" "}
                                                    {(
                                                        ((new Date(renderedAssignment.workStartTime.replace(/-/, "/")).valueOf() -
                                                            new Date(renderedAssignment.travelStartTime.replace(/-/, "/")).valueOf()) +
                                                            (new Date(renderedAssignment.travelEndTime.replace(/-/, "/")).valueOf() -
                                                                new Date(renderedAssignment.workEndTime.replace(/-/, "/")).valueOf())) /
                                                        3600000
                                                    ).toFixed(2)}
                                                </li>
                                                <li>
                                                    Work:{" "}
                                                    {(
                                                        (new Date(renderedAssignment.workEndTime.replace(/-/, "/")).valueOf() -
                                                            new Date(renderedAssignment.workStartTime.replace(/-/, "/")).valueOf()) /
                                                        3600000
                                                    ).toFixed(2)}
                                                </li>
                                                <li>
                                                    Total:{" "}
                                                    {(
                                                        (new Date(renderedAssignment.travelEndTime.replace(/-/, "/")).valueOf() -
                                                            new Date(renderedAssignment.travelStartTime.replace(/-/, "/")).valueOf()) /
                                                        3600000
                                                    ).toFixed(2)}
                                                </li>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td style={{ textAlign: "right" }}>
                                            <strong className="me-3">Selected Total:</strong>
                                        </td>
                                        <td>
                                            <li>
                                                Travel:{" "}
                                                {assignments
                                                    .filter(assignment => assignment.startTime.substring(0, 10) >= curStartDate && assignment.startTime.substring(0, 10) <= curEndDate)
                                                    .map(assignment => {
                                                        return (
                                                            (new Date(assignment.workStartTime.replace(/-/, "/")).valueOf() -
                                                                new Date(assignment.travelStartTime.replace(/-/, "/")).valueOf() +
                                                                (new Date(assignment.travelEndTime.replace(/-/, "/")).valueOf() -
                                                                    new Date(assignment.workEndTime.replace(/-/, "/")).valueOf())) /
                                                            3600000
                                                        );
                                                    })
                                                    .reduce(numberReducer, 0)
                                                    .toFixed(2)}
                                            </li>
                                            <li>
                                                Work:{" "}
                                                {assignments
                                                    .filter(assignment => assignment.startTime.substring(0, 10) >= curStartDate && assignment.startTime.substring(0, 10) <= curEndDate)
                                                    .map(
                                                        assignment =>
                                                            (new Date(assignment.workEndTime.replace(/-/, "/")).valueOf() -
                                                                new Date(assignment.workStartTime.replace(/-/, "/")).valueOf()) /
                                                            3600000
                                                    )
                                                    .reduce(numberReducer, 0)
                                                    .toFixed(2)}
                                            </li>
                                            <li>
                                                Total:{" "}
                                                {assignments
                                                    .filter(assignment => assignment.startTime.substring(0, 10) >= curStartDate && assignment.startTime.substring(0, 10) <= curEndDate)
                                                    .map(
                                                        assignment =>
                                                            (new Date(assignment.travelEndTime.replace(/-/, "/")).valueOf() -
                                                                new Date(assignment.travelStartTime.replace(/-/, "/")).valueOf()) /
                                                            3600000
                                                    )
                                                    .reduce(numberReducer, 0)
                                                    .toFixed(2)}
                                            </li>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <strong className="me-3">Total:</strong>
                                        </td>
                                        <td>
                                            <li>
                                                Travel:{" "}
                                                {assignments
                                                    .map(
                                                        assignment =>
                                                            (new Date(assignment.workStartTime.replace(/-/, "/")).valueOf() -
                                                                new Date(assignment.travelStartTime.replace(/-/, "/")).valueOf() +
                                                                (new Date(assignment.travelEndTime.replace(/-/, "/")).valueOf() -
                                                                    new Date(assignment.workEndTime.replace(/-/, "/")).valueOf())) /
                                                            3600000
                                                    )
                                                    .reduce(numberReducer, 0)
                                                    .toFixed(2)}
                                            </li>
                                            <li>
                                                Work:{" "}
                                                {assignments
                                                    .map(
                                                        assignment =>
                                                            (new Date(assignment.workEndTime.replace(/-/, "/")).valueOf() -
                                                                new Date(assignment.workStartTime.replace(/-/, "/")).valueOf()) /
                                                            3600000
                                                    )
                                                    .reduce(numberReducer, 0)
                                                    .toFixed(2)}
                                            </li>
                                            <li>
                                                Total:{" "}
                                                {assignments
                                                    .map(
                                                        assignment =>
                                                            (new Date(assignment.travelEndTime.replace(/-/, "/")).valueOf() -
                                                                new Date(assignment.travelStartTime.replace(/-/, "/")).valueOf()) /
                                                            3600000
                                                    )
                                                    .reduce(numberReducer, 0)
                                                    .toFixed(2)}
                                            </li>
                                        </td>
                                        <td>
                                            
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex align-items-center my-2">
                            <label htmlFor="approverName" className="fw-bold required-field">
                                Approver:
                            </label>
                            <div className="flex-grow-1 ms-2">
                                <AsyncFormSelect
                                    className="flex-grow-1"
                                    placeholder="Search approver..."
                                    required={true}
                                    name="approverId"
                                    inputId="approverName"
                                    fileName="common/searchUser.php"
                                    value={curApprover}
                                    onChange={next => {
                                        setApprover(next as SelectOption | null);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="d-flex align-items-center my-2">
                            <label htmlFor="notes" className="fw-bold">
                                Notes:
                            </label>
                            <textarea id="notes" name="notes" defaultValue={billing.notes ?? ""} className="form-control flex-grow-1 ms-2" />
                        </div>
                    </>
                ) : null}
                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button type="submit" className="btn btn-primary">
                        {keyword}
                    </button>
                    <button type="button" className="btn btn-dark" onClick={closeIt}>
                        Cancel
                    </button>
                </div>
            </div>
        </Form>
    );
}
